import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  selectCustomerEmailAddresses,
  selectEmailConversationById,
  selectEmailError,
  selectWrapupPrompt
} from 'common/store/genesys-cloud/conversations/emails/selectors/email-conversation.selectors';
import { ThemeService } from 'common/theme/theme.service';
import { GenesysEmailWorkItem } from 'common/workitems/interfaces/genesys-email-item.interface';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { combineLatest, ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import {
  selectDraftEmailMessageByConversationId,
  selectLastEmailMessageByConversationId
} from 'common/store/genesys-cloud/conversations/emails/selectors/email-message.selectors';
import {
  selectIntegrationsByConversation,
  selectOpenIntegrationPanels
} from 'common/store/genesys-cloud/work-item-integrations/selectors/work-item-integrations.selectors';
import {
  setAllowedIntegrationPanels,
  toggleIntegrationPanelVisibility
} from 'common/store/genesys-cloud/work-item-integrations/actions/work-item-integrations.actions';
import { closeConversationWorkItem } from 'common/store/genesys-cloud/conversations/common/actions/conversation-work-item.actions';
import { selectContactFullNameById } from 'common/store/genesys-cloud/external-contacts/selectors/external-contact.selectors';

@Component({
  selector: 'app-genesys-email-widget',
  templateUrl: './genesys-email-widget.component.html',
  styleUrls: ['./genesys-email-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class GenesysEmailWidgetComponent implements OnChanges, OnInit, OnDestroy {
  @Input() workItem: GenesysEmailWorkItem;

  themeClass: string;

  private readonly conversationIdSubject = new ReplaySubject<string>(1);

  private _conversationSubscription: Subscription;

  constructor(
    private readonly store: Store,
    private readonly workAreaManager: WorkAreaManager,
    private readonly themeService: ThemeService,
    private readonly rxEmitter: RxEmitter
  ) {}

  ngOnChanges({ workItem }: SimpleChanges): void {
    if (workItem) {
      this.conversationIdSubject.next(workItem.currentValue?.id);
    }
  }

  ngOnInit(): void {
    this._conversationSubscription = this.conversation$.subscribe((conversation) => {
      this.store.dispatch(setAllowedIntegrationPanels({ conversation }));
    });
  }

  ngOnDestroy(): void {
    this._conversationSubscription?.unsubscribe();
  }

  isExpandedMode(): boolean {
    return this.workAreaManager.isExpandedMode();
  }

  onMinimize(): void {
    this.store.dispatch(closeConversationWorkItem({ conversationId: this.workItem.id }));
  }

  onToggleExpanded(): void {
    this.rxEmitter.send(MessageBusEvents.ToggleExpandedMode, null);
    this.focusWorkItem();
  }

  onToggleIntegration(integrationId: string): void {
    this.store.dispatch(toggleIntegrationPanelVisibility({ conversationId: this.workItem.id, integrationId }));
  }

  readonly conversationId$ = this.conversationIdSubject.pipe(
    filter((conversationId) => !!conversationId),
    distinctUntilChanged()
  );

  readonly cssClassName$ = this.conversationId$.pipe(
    map((conversationId) => this.themeService.getThemeId(conversationId)),
    map((themeId) => this.themeService.getThemeCssClassName(themeId))
  );

  private readonly conversation$ = this.conversationId$.pipe(
    filter((conversationId) => !!conversationId),
    distinctUntilChanged(),
    switchMap((conversationId) => this.store.select(selectEmailConversationById(conversationId)))
  );

  readonly externalContactFullName$ = this.conversation$.pipe(
    map((conversation) => conversation.customerParticipant.externalContactId),
    distinctUntilChanged(),
    switchMap((contactId) => this.store.select(selectContactFullNameById(contactId)))
  );

  readonly name$ = this.conversation$.pipe(map((c) => c.customerParticipant?.name || c.customerParticipant?.address));

  readonly lastEmail$ = this.conversationId$.pipe(
    mergeMap((conversationId) => this.store.select(selectLastEmailMessageByConversationId(conversationId)))
  );

  readonly isLastEmailFromCustomer$ = combineLatest([this.conversation$, this.lastEmail$]).pipe(
    map(([conversation, lastEmail]) =>
      conversation.customerParticipant.emails?.some((e) => e.messageId === lastEmail?.id)
    )
  );

  readonly isWrapUpRequired$ = this.conversationId$.pipe(
    mergeMap((conversationId) => this.store.select(selectWrapupPrompt(conversationId)))
  );

  private readonly isProactiveEmail$ = this.conversation$.pipe(map((conversation) => conversation.isProactiveEmail));

  readonly isLoading$ = combineLatest([this.isProactiveEmail$, this.lastEmail$]).pipe(
    map(([isProactiveEmail, lastEmail]) => !isProactiveEmail && !lastEmail)
  );

  readonly agentEmailAddress$ = this.conversation$.pipe(
    map((c) => ({ name: c.agentParticipant.name, email: c.agentParticipant.address }))
  );

  readonly customerEmailAddresses$ = this.conversationId$.pipe(
    mergeMap((conversationId) => this.store.select(selectCustomerEmailAddresses(conversationId)))
  );

  readonly integrations$ = this.conversation$.pipe(
    switchMap((conversation) => this.store.select(selectIntegrationsByConversation(conversation)))
  );

  readonly agentParticipantEmailAddress = this.conversation$.pipe(map((c) => c.agentParticipant.address));

  readonly emailError$ = this.conversationId$.pipe(
    mergeMap((conversationId) => this.store.select(selectEmailError(conversationId)))
  );

  readonly draftEmail$ = this.conversationId$.pipe(
    switchMap((c) => this.store.select(selectDraftEmailMessageByConversationId(c)))
  );

  readonly agentParticipantId$ = this.conversation$.pipe(map((c) => c.agentParticipant.id));

  readonly openIntegrationPanels$ = this.conversationId$.pipe(
    switchMap((conversationId) => this.store.select(selectOpenIntegrationPanels(conversationId)))
  );

  readonly isEmptyProactiveEmail$ = this.conversation$.pipe(map((c) => c.isProactiveEmail && !c.lastEmailMessageId));

  private focusWorkItem(): void {
    this.workAreaManager.focusWorkItem(this.workItem.id);
  }
}
